import {
  Box,
  TableCell,
  TableRow,
  Table,
  TableContainer,
  TableHead,
  Typography,
  TableBody,
  Skeleton,
  Button,
  Paper,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { getValidToken } from "../utils/authUtils";
import { formatDateWithTime } from "../utils/utils";
import {
  commonTableContainerProps,
  tableHeaderStyles,
  tableRowStyles,
} from "../utils/styles";

export const getActivities = async (page: number, limit: number) => {
  try {
    const token = await getValidToken();
    const queryParams = new URLSearchParams({
      page: page?.toString() || "1",
      limit: limit?.toString() || "10",
    });
    const url = `${process.env.REACT_APP_API_HOST}recent-activities/company?${queryParams}`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.data?.isSuccess) {
      return response.data.data;
    } else {
      return [];
    }
  } catch (error) {
    return [];
  }
};

interface CompanyActivityProps {
  isLoading: boolean;
  companyActivity: any[];
  isLoadingMore: boolean;
  hasMore: boolean;
  limit: number;
  handleShowMore: () => void;
}

const CompanyActivity = ({
  isLoading,
  companyActivity,
  isLoadingMore,
  hasMore,
  limit,
  handleShowMore,
}: CompanyActivityProps) => {
  return (
    <Box
      sx={{
        height: "350px",
        overflow: "auto",
        "&::-webkit-scrollbar": {
          width: "3px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#f1f1f1",
          borderRadius: "3px",
        },

        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#c1c1c1",
          borderRadius: "3px",
          "&:hover": {
            backgroundColor: "#a8a8a8",
          },
        },
        "&::-webkit-scrollbar-corner": {
          backgroundColor: "#f1f1f1",
        },
        backgroundColor: "white",
        borderRadius: "5px",
        boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.1)",
        px: 1,
        pb: 1,
      }}
    >
      <Typography
        sx={{
          position: "sticky",
          top: 0,
          px: 1,
          py: 1,
          borderBottom: "1px solid #e0e0e0",
          bgcolor: "white",
          zIndex: 1,
        }}
        variant="body1"
        gutterBottom
      >
        Today's User Activity
      </Typography>
      {isLoading ? (
        <Skeleton variant="rectangular" height={"290px"} />
      ) : companyActivity?.length > 0 ? (
        <TableContainer>
          <Table
            size="small"
            stickyHeader
            {...commonTableContainerProps}
            component={Paper}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...tableHeaderStyle, bgcolor: "grey.50" }}>
                  User
                </TableCell>
                <TableCell sx={{ ...tableHeaderStyle, bgcolor: "grey.50" }}>
                  Last Login
                </TableCell>
                <TableCell sx={{ ...tableHeaderStyle, bgcolor: "grey.50" }}>
                  Activity
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companyActivity?.map((activity) => (
                <TableRow sx={tableRowStyles}>
                  <TableCell sx={tableCellStyle}>{activity?.name}</TableCell>
                  <TableCell sx={tableCellStyle}>
                    {formatDateWithTime(activity?.lastLoginAt)}
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    {activity?.recentActivities?.length || 0}
                  </TableCell>
                </TableRow>
              ))}
              {hasMore && (
                <Box
                  sx={{
                    display: "flex",
                  }}
                >
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleShowMore}
                    disabled={isLoadingMore}
                    sx={{
                      textTransform: "none",
                      fontSize: "0.7rem",
                      fontWeight: 400,
                      fontStyle: "italic",
                      textDecoration: isLoadingMore ? "none" : "underline",
                      color: "primary.main",
                      p: 0,
                      px: 1,
                      "&:hover": {
                        textDecoration: isLoadingMore ? "none" : "underline",
                      },
                    }}
                  >
                    {isLoadingMore ? "Loading..." : "Load More"}
                  </Button>
                </Box>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography
          sx={{
            p: 1,
            fontStyle: "italic",
            fontSize: 10,
            textAlign: "center",
            fontWeight: 400,
          }}
          variant="caption"
        >
          No other users found
        </Typography>
      )}
    </Box>
  );
};

export default CompanyActivity;

const tableHeaderStyle = {
  fontSize: 10,
  fontWeight: 700,
  py: 0.5,
  backgroundColor: "#f5f5f5",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const tableCellStyle = {
  fontSize: 10,
  py: 0.8,
  fontWeight: 500,
};
